import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatMoney, formatDate, VADE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, FileText, Printer, Building2, Download } from "lucide-react";
import { toast } from "sonner";

export default function Statement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/companies/${id}/statement`);
        setData(data);
      } catch (e) {
        toast.error("Ekstre alınamadı");
        navigate("/companies");
      }
    })();
  }, [id]);

  async function downloadPdf() {
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF();
      doc.setFont("helvetica");
      doc.setFontSize(18);
      doc.text("Cari Ekstre", 14, 20);
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Firma: ${data.company.name}`, 14, 30);
      doc.text(`Vade: ${VADE_LABELS[data.company.vade_term]} · Vade Farki: %${data.company.vade_farki_pct}`, 14, 36);
      doc.text(`Tarih: ${new Date().toLocaleDateString("tr-TR")}`, 14, 42);

      autoTable(doc, {
        startY: 50,
        head: [["Tarih", "Aciklama", "Tip", "Borc", "Alacak", "Bakiye"]],
        body: data.rows.map((r) => [
          formatDate(r.issue_date),
          r.description,
          r.type,
          r.debit ? r.debit.toFixed(2) + " TL" : "-",
          r.credit ? r.credit.toFixed(2) + " TL" : "-",
          r.balance.toFixed(2) + " TL",
        ]),
        headStyles: { fillColor: [11, 17, 28], textColor: [245, 158, 11] },
        styles: { fontSize: 9 },
      });

      const y = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Toplam Satis: ${data.summary.total_sales.toFixed(2)} TL`, 14, y);
      doc.text(`Toplam Alis: ${data.summary.total_purchases.toFixed(2)} TL`, 14, y + 6);
      doc.text(`Toplam Vade Farki: ${data.summary.total_vade_farki.toFixed(2)} TL`, 14, y + 12);
      doc.setFont("helvetica", "bold");
      doc.text(`Bakiye: ${data.summary.balance.toFixed(2)} TL`, 14, y + 20);

      doc.save(`cari-ekstre-${data.company.name.replace(/\s/g, "-")}.pdf`);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    }
  }

  if (!data) return <div className="text-slate-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="statement-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/companies")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Cari Ekstre</div>
            <h1 className="text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Building2 className="w-7 h-7 text-amber-400" /> {data.company.name}
            </h1>
            <div className="text-sm text-slate-400 mt-1 flex items-center gap-3">
              <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/25">{VADE_LABELS[data.company.vade_term]}</Badge>
              <span>Vade Farkı: %{data.company.vade_farki_pct}</span>
              <span>·</span>
              <span>{data.company.email || "e-posta yok"}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="border-white/10" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" /> Yazdır
          </Button>
          <Button onClick={downloadPdf} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF İndir
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-elevated rounded-2xl p-4 border border-emerald-500/25">
          <div className="text-[10px] mono uppercase tracking-widest text-emerald-300">Toplam Satış</div>
          <div className="text-2xl font-bold tabular mt-1">{formatMoney(data.summary.total_sales)}</div>
        </div>
        <div className="card-elevated rounded-2xl p-4 border border-red-500/25">
          <div className="text-[10px] mono uppercase tracking-widest text-red-300">Toplam Alış</div>
          <div className="text-2xl font-bold tabular mt-1">{formatMoney(data.summary.total_purchases)}</div>
        </div>
        <div className="card-elevated rounded-2xl p-4 border border-amber-500/25">
          <div className="text-[10px] mono uppercase tracking-widest text-amber-300">Vade Farkı</div>
          <div className="text-2xl font-bold tabular mt-1">{formatMoney(data.summary.total_vade_farki)}</div>
        </div>
        <div className={`card-elevated rounded-2xl p-4 border ${data.summary.balance >= 0 ? "border-emerald-500/40" : "border-red-500/40"}`}>
          <div className="text-[10px] mono uppercase tracking-widest text-slate-300">Bakiye</div>
          <div className={`text-2xl font-bold tabular mt-1 ${data.summary.balance >= 0 ? "text-emerald-300" : "text-red-300"}`}>
            {formatMoney(data.summary.balance)}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">{data.summary.balance >= 0 ? "Firma bize borçlu" : "Biz firmaya borçluyuz"}</div>
        </div>
      </div>

      <div className="card-elevated rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
          <FileText className="w-4 h-4 text-amber-400" />
          <span className="font-semibold">Hareketler</span>
          <span className="text-xs text-slate-500">({data.rows.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
                <th className="py-3 px-5">Tarih</th>
                <th className="py-3 px-5">Açıklama</th>
                <th className="py-3 px-5">Kategori</th>
                <th className="py-3 px-5">Tip</th>
                <th className="py-3 px-5 text-right">Borç</th>
                <th className="py-3 px-5 text-right">Alacak</th>
                <th className="py-3 px-5 text-right">Bakiye</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-slate-500">Hareket yok</td></tr>
              )}
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`statement-row-${r.id}`}>
                  <td className="py-3 px-5 tabular text-slate-300">{formatDate(r.issue_date)}</td>
                  <td className="py-3 px-5">{r.description}</td>
                  <td className="py-3 px-5 text-slate-400 text-xs">{r.category || "-"}</td>
                  <td className="py-3 px-5">
                    <Badge className={r.type === "sale" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25" : "bg-red-500/15 text-red-300 border-red-500/25"}>
                      {r.type === "sale" ? "Satış" : r.type === "purchase" ? "Alış" : r.type}
                    </Badge>
                  </td>
                  <td className="py-3 px-5 tabular text-right text-emerald-300">{r.debit ? formatMoney(r.debit) : "-"}</td>
                  <td className="py-3 px-5 tabular text-right text-red-300">{r.credit ? formatMoney(r.credit) : "-"}</td>
                  <td className={`py-3 px-5 tabular text-right font-semibold ${r.balance >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                    {formatMoney(r.balance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
